import React, { useCallback, useMemo } from 'react';
import { DrillDownParams } from '@models/analysisVisualization';
import { Analysis } from '@models/analysis';
import { Updater } from 'use-immer';
import { Form } from 'react-bootstrap';
import MetricSelector from '../MetricSelector.component';
import DimensionSelector from '../DimensionSelector.component';
import styled from 'styled-components';
import Chart from 'react-apexcharts';
import Dropdown, { MultiDropdown } from '@components/form/Dropdown.component';
import { useFeatureFlagValue } from '@services/featureFlags/featureFlag.service';
import BigMetricCard from '@components/metric/BigMetricCard.component';
import { formatValue } from '@services/formatting.service';

const Styles = styled.div`
    height: 100%;
    overflow: hidden;
`;


export interface CustomerAcquisitionAnalysisParameters {
    period_length_in_days: number;
    chart_format: string;
    included_stages?: string[];
}


interface CustomerAcquisitionData {
    render_type?: string;
    options: {
        dataset1?: Array<{
            CLASSIFICATION: string;
            NUMBER_OF_CUSTOMERS: number;
            PERIOD: string;
            // Add other fields as needed
        }>;
        columns?: Array<{
            entityType: string;
            entityIdx: number;
            field: string;
            headerName: string;
            sortable: boolean;
            filter: boolean;
        }>;
        data?: Array<any>;
        total_records?: number;
    };
}

interface PremiumAnalysisFormProps {
    analysis: Analysis;
    setAnalysis: Updater<Analysis>;
    measureOptions: Array<{ value: string; label: string }>;
    dimensionOptions: Array<{ value: string; label: string }>;
    disabledMeasureIds?: string[];
    disabledDimensionIds?: string[];
}

interface PremiumAnalysisVisualizationProps<T> {
    data: T;
    analysis: Analysis;
    onDrillDown?: (params: DrillDownParams) => void;
    sortOrder?: any[];
    onToggleSort?: (entityType: string, entityId: string) => void;
    onRemoveSort?: (entityType: string, entityId: string) => void;
    onChangePage?: (newPage: number, pageSize: number) => void;
    pageSize?: number;
    currentPage?: number;
}

export const CustomerAcquisitionForm: React.FC<PremiumAnalysisFormProps> = ({
    analysis,
    setAnalysis,
    measureOptions,
    dimensionOptions,
    disabledMeasureIds,
    disabledDimensionIds
}) => {

    const CHART_FORMAT_OPTIONS = [{
        label: 'Area Chart',
        value: 'AREA_CHART',
    }, {
        label: 'Table - By Customer',
        value: 'TABLE_BY_CUSTOMER',
    }, {
        label: 'Single Value: Retained Customers',
        value: 'RETAINED',
    }, {
        label: 'Single Value: Acquired Customers',
        value: 'ACQUIRED',
    }, {
        label: 'Single Value: Reactivated Customers',
        value: 'REACTIVATED',
    },
    {
        label: 'Single Value: Churned Customers',
        value: 'CHURNED',
    }, {
        label: 'Single Value: At Risk Customers',
        value: 'AT_RISK',
    }]

    const STAGE_OPTIONS = [{
        label: 'Reactivated',
        value: 'REACTIVATED',
    }, {
        label: 'Churned',
        value: 'CHURNED',
    }, {
        label: 'Acquired',
        value: 'ACQUIRED',
    }, {
        label: 'Retained',
        value: 'RETAINED',
    }, {
        label: 'At Risk',
        value: 'AT_RISK',
    }, {
        label: 'Active',
        value: 'ACTIVE',
    }]

    const customerAcquisitionFormat = useFeatureFlagValue('customer-acquisition-format', false);

    return (
        <>
            <Form.Group className="mb-3">
                <Form.Label>Conversion Metric</Form.Label>
                <MetricSelector
                    disableMulti
                    options={measureOptions}
                    selected={analysis.measure_ids[0] ? [analysis.measure_ids[0]] : []}
                    onChange={(newVal) => {
                        setAnalysis(draft => {
                            draft.measure_ids[0] = newVal[0];
                        });
                    }}
                    disabledOptionIds={disabledMeasureIds}
                />
                <Form.Text>Select a metric tracking the conversion event you want to use for retention analysis. For example "sales" or "orders".</Form.Text>
            </Form.Group>


            <Form.Group className="mb-3">
                <Form.Label>Date Interval</Form.Label>
                <DimensionSelector
                    disableMulti
                    dateDimGranularityOnly
                    options={dimensionOptions}
                    selected={analysis.dimension_ids[0] ? [analysis.dimension_ids[0]] : []}
                    onChange={(newVal) => {
                        setAnalysis(draft => {
                            draft.dimension_ids[0] = newVal[0];
                        });
                    }}
                    disabledOptionIds={disabledDimensionIds}
                />
                <Form.Text>
                    Choose the granularity of your data.
                </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
                <Form.Label>Reactivation Window</Form.Label>
                <Form.Range 
                    min={1}
                    max={365}
                    value={analysis.custom_parameters?.period_length_in_days || 30}
                    onChange={(e) => {
                        setAnalysis(draft => {
                            if (!draft.custom_parameters) {
                                draft.custom_parameters = {};
                            }
                            draft.custom_parameters.period_length_in_days = parseInt(e.target.value);
                        });
                    }}
                />
                <div className="text-primary">
                    {analysis.custom_parameters?.period_length_in_days == 1 && <>1 day</>}
                    {(!analysis.custom_parameters?.period_length_in_days || analysis.custom_parameters?.period_length_in_days > 1) && <>{analysis.custom_parameters?.period_length_in_days || 30} days</>}
                </div>
                
                <Form.Text>
                    How many days must pass before a customer is considered "churned" and can later be reactivated? Customers will be flagged as at-risk if they've been inactive for 75% of this number.
                </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
                <Form.Label>Customer Dimension</Form.Label>
                <DimensionSelector
                    disableMulti
                    options={dimensionOptions}
                    selected={analysis.dimension_ids[1] ? [analysis.dimension_ids[1]] : []}
                    onChange={(newVal) => {
                        setAnalysis(draft => {
                            draft.dimension_ids[1] = newVal[0];
                        });
                    }}
                    disabledOptionIds={disabledDimensionIds}
                />
            </Form.Group>
            {customerAcquisitionFormat &&
                <Form.Group className="mb-3">
                    <Form.Label>Chart Format</Form.Label>
                    <Dropdown
                        options={CHART_FORMAT_OPTIONS}
                        isClearable={false}
                        selected={analysis.custom_parameters?.chart_format || CHART_FORMAT_OPTIONS[0].value}
                        onChange={(v) => {
                            setAnalysis(draft => {
                                if (!draft.custom_parameters) draft.custom_parameters = {};
                                const previousFormat = draft.custom_parameters.chart_format;
                                draft.custom_parameters.chart_format = v;
                                const formatsWithStages = ['AREA_CHART', 'TABLE_BY_CUSTOMER'];
                                const previousHadStages = !previousFormat || formatsWithStages.includes(previousFormat);
                                const newHasStages = formatsWithStages.includes(v);
                                if (newHasStages && !previousHadStages) {
                                    draft.custom_parameters.included_stages = STAGE_OPTIONS.map(s => s.value);
                                }
                            });
                        }}
                    />
                    <Form.Text>Choose how you would like to view your data.</Form.Text>
                </Form.Group>
            }
            {customerAcquisitionFormat && (analysis.custom_parameters?.chart_format === 'AREA_CHART' || analysis.custom_parameters?.chart_format === 'TABLE_BY_CUSTOMER' || !analysis.custom_parameters?.chart_format) &&
                <Form.Group className="mb-3">
                    <Form.Label>Included Stages</Form.Label>
                    <MultiDropdown
                        options={STAGE_OPTIONS}
                        selected={analysis.custom_parameters?.included_stages || STAGE_OPTIONS.map(s => s.value)}
                        onChange={(v) => {
                            setAnalysis(draft => {
                                if (!draft.custom_parameters) draft.custom_parameters = {};
                                draft.custom_parameters.included_stages = v;
                            });
                        }}
                    />
                    <Form.Text>Select which lifecycle stages to include.</Form.Text>
                </Form.Group>
            }            
        </>
    );
};

export const CustomerAcquisitionVisualization: React.FC<PremiumAnalysisVisualizationProps<CustomerAcquisitionData>> = ({
    data,
    analysis,
    onDrillDown
}) => {
    const chartFormat = useMemo(() => {
        return analysis.custom_parameters?.chart_format || 'AREA_CHART';
    }, [analysis.custom_parameters?.chart_format]);

    const rows = useMemo(() => {
        if (!data || !data.options || !data.options.dataset1) {
            return [];
        }
        
        if (chartFormat === 'AREA_CHART') {
            return data.options.dataset1.sort((a, b) => {
                if (!a.PERIOD || !b.PERIOD) return 0;
                return a.PERIOD.localeCompare(b.PERIOD);
            });
        }
        
        return data.options.dataset1;
    }, [data, chartFormat]);

    const drilldown = useCallback((params: DrillDownParams) => {
        if (onDrillDown) {
            onDrillDown(params);
        }
    }, [onDrillDown]);

    const formatLabel = useCallback((classification: string) => {
        switch(classification) {
            case '03 - Acquired': return 'Acquired Customers';
            case '01 - Reactivated': return 'Reactivated Customers';
            case '04 - Retained': return 'Retained Customers';
            case '05 - At Risk': return 'At Risk Customers';
            case '02 - Churned': return 'Churned Customers';
            case '09 - Active': return 'Active Customers';
            default: return classification + ' Customers';
        }
    }, []);

    const series = useMemo(() => {
        if (chartFormat !== 'AREA_CHART') return [];
        
        const seriesByName: {
            [name: string]: number[];
        } = {};

        if (!rows) {
            return [];
        }

        rows.forEach(row => {
            if (!seriesByName[row.CLASSIFICATION]) {
                seriesByName[row.CLASSIFICATION] = [];
            }
            seriesByName[row.CLASSIFICATION].push(row.NUMBER_OF_CUSTOMERS);
        });

        return Object.keys(seriesByName).map(name => {
            return {
                name: formatLabel(name),
                data: seriesByName[name],
            };
        });
    }, [rows, chartFormat, formatLabel]);

    const xAxisCategories = useMemo(() => {
        if (chartFormat !== 'AREA_CHART') return [];
        
        // Only unique periods
        const uniquePeriods = new Set(rows.map(row => row.PERIOD).filter(Boolean));
        return Array.from(uniquePeriods);
    }, [rows, chartFormat]);

    if (chartFormat !== 'AREA_CHART') {
        const singleClassificationData = rows[0];
        
        if (!singleClassificationData) {
            return (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="text-muted">No data available</div>
                </div>
            );
        }



        return (
                <BigMetricCard
                    title={formatLabel(singleClassificationData.CLASSIFICATION)}
                    metric={formatValue({
                        value: singleClassificationData.NUMBER_OF_CUSTOMERS,
                        format: 'NUMERIC'
                    })}
                    loading={false}
                    icon=""
                />
        );
    }

    return (
        <div style={{
            height: '100%',
            width: '100%',
            overflow: 'auto'
        }}>
            <Styles>
                <Chart
                    options={{
                        chart: {
                            type: 'area',
                            height: '100%',
                            toolbar: {
                                show: false
                            },
                            animations: {
                                enabled: true
                            }
                        },
                        dataLabels: {
                            enabled: false,
                        },
                        stroke: {
                            curve: 'smooth',
                            width: 2,
                        },
                        tooltip: {
                            enabled: true,
                            shared: true,
                        },
                        legend: {
                            position: 'top',
                            horizontalAlign: 'right',
                        },
                        yaxis: {
                            min: 0,
                            forceNiceScale: true,
                        },
                        xaxis: {
                            categories: xAxisCategories,
                        },

                    }}
                    series={series}
                    type="area"
                    height="100%"
                />
            </Styles>
        </div>
    );
};